import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import { TableVirtualRows } from "./TableVirtualRows";
import { DocumentsFilteredEmptyState } from "../../DocumentsFilteredEmptyState";
export const TableEmpty = observer(function TableEmpty(props: DocumentTablePresentation) {
  const { isEmpty, t, activeSearchQuery, docFiltersContext } = props;
  // A refresh never lands here: DocumentListTable marks the table rendered in
  // its first render, so `isEmpty` already excludes the loading overlay.
  if (!isEmpty) return null;
  if (props.hasActiveFilters) {
    return (
      <DocumentsFilteredEmptyState
        onClearFilters={props.onClearFilters ?? docFiltersContext?.clearFilters}
      />
    );
  }
  return (
    <div className="flex items-center justify-center p-12">
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm font-medium text-muted-foreground">
          {t.documents.table.noDocumentsFound}
        </p>
        <p className="text-xs text-muted-foreground">
          {activeSearchQuery
            ? t.documents.table.noSearchResultsHint
            : t.documents.table.noDocumentsHint}
        </p>
      </div>
    </div>
  );
});

export const TableScrolling = observer(function TableScrolling(props: DocumentTablePresentation) {
  const {
    isEmpty,
    setScrollContainer,
    scrollMaxHeight,
    selectionToolbarOpen,
    shiftHeld,
    handleSelectAll,
    heightLimit,
    showLoadingOverlay,
  } = props;
  return (
    !isEmpty && (
      <div
        ref={setScrollContainer}
        style={{ maxHeight: scrollMaxHeight }}
        className={cn(
          "relative overflow-auto",
          selectionToolbarOpen && "pb-24",
          shiftHeld && "select-none"
        )}
        onClick={(e) => {
          // Clear selection when clicking on empty space in scroll area.
          // Use handleSelectAll(false), not store.selectAll(false): the
          // latter only clears store.documents (empty on /projects) and
          // leaves folder selection untouched.
          if (e.target === e.currentTarget) {
            handleSelectAll(false);
          }
        }}
      >
        {/* Inner container with total height for scroll (+ sentinel height when applicable) */}
        <TableVirtualCanvas {...props} />

        {/* Trailing space so the last row scrolls clear of the chat pill.
                  Inside the scroller, not reserved off its height — the table
                  itself still reaches the bottom of the window. A host with
                  content after the list (the Brief page) turns it off. */}
        {heightLimit?.chatClearance !== false && <ChatLauncherSpacer />}

        {showLoadingOverlay && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/35">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    )
  );
});

export const TableVirtualCanvas = observer(function TableVirtualCanvas(
  props: DocumentTablePresentation
) {
  const { virtualTotalSize, hasMore, isLoadingMore } = props;
  return (
    <div
      style={{
        height: `${virtualTotalSize + (hasMore || isLoadingMore ? 48 : 0)}px`,
        position: "relative",
      }}
    >
      {/* Only render visible rows */}
      <TableVirtualRows {...props} />
      {/* Infinite scroll sentinel - triggers loading more when visible */}
      <TableSentinel {...props} />
    </div>
  );
});

export const TableSentinel = observer(function TableSentinel(props: DocumentTablePresentation) {
  const { hasMore, isLoadingMore, sentinelRef, virtualTotalSize } = props;
  return (
    (hasMore || isLoadingMore) && (
      <div
        ref={sentinelRef}
        className="flex h-12 items-center justify-center"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualTotalSize}px)`,
        }}
      >
        {isLoadingMore && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
      </div>
    )
  );
});
